import { useState } from "react";
import { router } from "@inertiajs/react";
import { BookOpen } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatNumber, pluralize } from "@/lib/format";
import { timeAgo } from "@/lib/timeAgo";

/**
 * Everything logged for a child — by this parent, other family members
 * or teachers. A parent can fix or undo only their own entries.
 */
export default function ReadingLog({ logs, totalMinutes, firstName }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-accent" /> Reading log</CardTitle>
        <CardDescription>{pluralize(logs.length, "entry", "entries")} · {formatNumber(totalMinutes)} minutes</CardDescription>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Reading you or {firstName}'s teacher logs will show up here.</p>
        ) : (
          <ul className="divide-y divide-border">
            {logs.map((log) => <LogRow key={log.id} log={log} />)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// 44px tall on phones, compact on wider screens.
const TAP = "h-11 md:h-9";

function whoLogged(log) {
  if (log.source === "parent") return log.logged_by_current_user ? "You" : "Family";
  return "Teacher";
}

function LogRow({ log }) {
  const [mode, setMode] = useState("idle"); // idle | editing | confirming-undo
  const [draft, setDraft] = useState(String(log.minutes));
  const [busy, setBusy] = useState(false);
  const draftValid = /^\d+$/.test(draft) && Number(draft) >= 1 && Number(draft) <= 1440;

  const save = (e) => {
    e?.preventDefault();
    if (!draftValid || busy) return;
    if (Number(draft) === log.minutes) return setMode("idle");
    setBusy(true);
    router.patch(log.edit_path, { minutes: Number(draft) }, {
      preserveScroll: true,
      onSuccess: () => setMode("idle"),
      onFinish: () => setBusy(false),
    });
  };

  const undo = () => {
    if (busy) return;
    setBusy(true);
    router.delete(log.undo_path, { preserveScroll: true, onFinish: () => setBusy(false) });
  };

  return (
    <li className="flex flex-wrap items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        {mode === "editing" ? (
          <form onSubmit={save} className="flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min="1"
              max="1440"
              autoFocus
              aria-label="Minutes"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") setMode("idle"); }}
              className="h-11 w-24 rounded-lg border border-input bg-card px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <span className="text-sm text-muted-foreground">minutes{log.note && ` · ${log.note}`}</span>
          </form>
        ) : (
          <>
            <p className="text-sm text-foreground">
              <span className="font-medium font-tabular">{log.minutes} min</span>
              {log.note && <span className="text-muted-foreground"> · {log.note}</span>}
            </p>
            <p className="text-xs text-muted-foreground">{timeAgo(log.logged_at)} · {whoLogged(log)}</p>
          </>
        )}
      </div>

      {mode === "editing" ? (
        <div className="flex gap-1">
          <Button size="sm" variant="secondary" className={TAP} onClick={save} disabled={!draftValid || busy}>Save</Button>
          <Button size="sm" variant="ghost" className={TAP} onClick={() => setMode("idle")}>Cancel</Button>
        </div>
      ) : mode === "confirming-undo" ? (
        <div className="flex items-center gap-1">
          <span className="text-sm text-muted-foreground">Remove it?</span>
          <Button size="sm" variant="ghost" className={`${TAP} text-destructive hover:text-destructive`} onClick={undo} disabled={busy}>Remove</Button>
          <Button size="sm" variant="ghost" className={TAP} onClick={() => setMode("idle")}>Keep</Button>
        </div>
      ) : (
        (log.edit_path || log.undo_path) && (
          <div className="flex gap-1">
            {log.edit_path && (
              <Button size="sm" variant="ghost" className={TAP} onClick={() => { setDraft(String(log.minutes)); setMode("editing"); }}>Edit</Button>
            )}
            {log.undo_path && <Button size="sm" variant="ghost" className={TAP} onClick={() => setMode("confirming-undo")}>Undo</Button>}
          </div>
        )
      )}
    </li>
  );
}
